import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';

interface RightPanelProps {
  title: string;
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  children: ReactNode;
  /** Pixels from the right edge of the viewport. Animates when changed. Default: 0. */
  rightOffset?: number;
  /** z-index for stacking. Default: 50. */
  zIndex?: number;
}

export default function RightPanel({
  title,
  onClose,
  onEdit,
  onDelete,
  children,
  rightOffset = 0,
  zIndex = 50,
}: RightPanelProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Slide-in on mount
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id1 = requestAnimationFrame(() => {
      const id2 = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id2);
    });
    return () => cancelAnimationFrame(id1);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (!menuOpen) return;
    function handleOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setConfirmingDelete(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [menuOpen]);

  const isMobile = useIsMobile();
  const hasMenu = Boolean(onEdit || onDelete);

  return (
    <>
      {/* Backdrop: mobile only — tap outside to close */}
      {isMobile && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 199,
          }}
        />
      )}
    <div
      style={{
        position: 'fixed',
        top: isMobile ? 0 : 'var(--nav-height, 54px)',
        right: isMobile ? 0 : rightOffset,
        bottom: 0,
        width: isMobile ? '100%' : 440,
        background: 'var(--color-surface)',
        borderLeft: '1px solid var(--color-border)',
        boxShadow: '-4px 0 20px rgba(16,24,40,0.08)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: isMobile ? 200 : zIndex,
        transform: entered ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.32s cubic-bezier(0.16, 1, 0.3, 1), right 0.32s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 16px',
          borderBottom: '1px solid var(--color-border)',
          flexShrink: 0,
          gap: 8,
        }}
      >
        {/* Close button — square */}
        <button
          type="button"
          onClick={onClose}
          title="Close (Esc)"
          style={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            lineHeight: 1,
            flexShrink: 0,
            background: 'none',
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            cursor: 'pointer',
            color: 'var(--color-text-muted)',
            padding: 0,
          }}
        >
          ›
        </button>

        {/* Title */}
        <span
          style={{
            flex: 1,
            textAlign: 'center',
            fontSize: 11,
            color: 'var(--color-text-muted)',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontWeight: 600,
          }}
        >
          {title}
        </span>

        {/* ••• menu or spacer */}
        {hasMenu ? (
          <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setMenuOpen(v => !v)}
              title="More options"
              style={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                lineHeight: 1,
                background: menuOpen ? 'var(--color-border)' : 'none',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
                cursor: 'pointer',
                color: 'var(--color-text-muted)',
                padding: 0,
                letterSpacing: 1,
              }}
            >
              •••
            </button>

            {menuOpen && (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 4px)',
                  minWidth: 150,
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  zIndex: 100,
                  overflow: 'hidden',
                }}
              >
                {onEdit && (
                  <button
                    type="button"
                    onClick={() => { onEdit(); setMenuOpen(false); }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 16px',
                      fontSize: 13,
                      textAlign: 'left',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-text)',
                    }}
                  >
                    Edit
                  </button>
                )}
                {onEdit && onDelete && (
                  <div style={{ height: 1, background: 'var(--color-border)', margin: '0 8px' }} />
                )}
                {onDelete && !confirmingDelete && (
                  <button
                    type="button"
                    onClick={() => setConfirmingDelete(true)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 16px',
                      fontSize: 13,
                      textAlign: 'left',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-danger, #dc2626)',
                    }}
                  >
                    Delete
                  </button>
                )}
                {onDelete && confirmingDelete && (
                  <div style={{ padding: '12px 16px' }}>
                    <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--color-text-muted)' }}>
                      Delete this task?
                    </p>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => { onDelete(); setMenuOpen(false); setConfirmingDelete(false); }}
                        style={{
                          fontSize: 12,
                          color: '#fff',
                          background: 'var(--color-danger, #dc2626)',
                          border: 'none',
                          borderRadius: 4,
                          padding: '5px 12px',
                          cursor: 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        Yes, delete
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingDelete(false)}
                        style={{
                          fontSize: 12,
                          background: 'none',
                          border: '1px solid var(--color-border)',
                          borderRadius: 4,
                          padding: '5px 12px',
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{ width: 32, flexShrink: 0 }} />
        )}
      </div>

      {/* Content — children manage their own scroll */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
    </div>
    </>
  );
}
